export const MIN = 60_000;
export const HOUR = 3_600_000;
export const DAY = 86_400_000;

const pad = (n: number) => String(n).padStart(2, "0");

export const fmtClock = (ts: number) => {
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const dayKey = (ts: number) => {
  const d = new Date(ts);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const isSunday = (ts: number) => new Date(ts).getDay() === 0;

export function startOfWeek(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime() - ((d.getDay() + 6) % 7) * DAY;
}

export function hoursLabel(h: number): string {
  if (!h) return "0h00";
  const hh = Math.floor(h);
  return `${hh}h${pad(Math.round((h - hh) * 60))}`;
}

/** hh:mm:ss for a running shift. Zero-padded so the width never changes as it
 *  ticks — pair it with tabular figures or the whole row jitters. */
export function elapsedLabel(ms: number): string {
  const h = Math.floor(ms / HOUR);
  const m = Math.floor((ms % HOUR) / MIN);
  const s = Math.floor((ms % MIN) / 1000);
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SHORT_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Tue" — the register's day column.
 *  Spelled out rather than left to toLocaleDateString: the JSX pinned en-ZA,
 *  and on a handset set to another locale the register would silently change
 *  language mid-document. A register is a legal record; it reads one way. */
export const fmtDay = (ts: number) => SHORT_DAYS[new Date(ts).getDay()];

/** "02 Sep" */
export function fmtDate(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getDate())} ${SHORT_MONTHS[d.getMonth()]}`;
}
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/** "Tuesday 2 September" — the date a person would say out loud. */
export function fmtLongDate(ts: number): string {
  const d = new Date(ts);
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  ZAR: "R", NAD: "N$", BWP: "P", USD: "US$", KES: "KSh", GBP: "£",
};
export const currencySymbol = (code: string) => CURRENCY_SYMBOLS[code] ?? code;

/**
 * "R 1 234.56". Grouped by hand rather than through toLocaleString for the
 * same reason the dates are: Hermes ships a cut-down Intl, so locale output
 * varies by device. A wage figure that renders differently on two handsets is
 * not something to leave to chance.
 *
 * The separators are NO-BREAK spaces, not ordinary ones. With a plain space
 * "R 2 472.01" wraps mid-number in a narrow column and reads as "R 2" above
 * "472.01" — which is not a smaller wage, it is a different one.
 */
const NBSP = " ";
export function money(value: number, currency = "ZAR"): string {
  const [whole, cents] = Math.abs(value).toFixed(2).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${value < 0 ? "-" : ""}${currencySymbol(currency)}${NBSP}${grouped}.${cents}`;
}
